/**
 * @license
 * author: Jacco Meijer (https://www.jaccomeijer.nl)
 * any-element v6.28.11
 * Released under the MIT license.
 */
import { jsx } from '@emotion/react';
import { jsx as jsx$1 } from 'react/jsx-runtime';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

var anyReset = {
  boxSizing: 'border-box'
};
var htmlReset = {
  lineHeight: 1.15,
  WebkitTextSizeAdjust: '100%'
};
var bodyReset = {
  margin: 0
};
var mainReset = {
  display: 'block'
};
var h1Reset = {
  fontSize: '2em',
  margin: '0.67em 0'
};
var hrReset = {
  boxSizing: 'content-box',
  height: 0,
  overflow: 'visible'
};
var preReset = {
  fontFamily: 'monospace, monospace',
  fontSize: '1em'
};
var aReset = {
  backgroundColor: 'transparent'
};
var abbrReset = {
  '&[title]': {
    borderBottom: 'none',
    textDecoration: 'underline dotted'
  }
};
var strongReset = {
  fontWeight: 'bolder'
};
var codeReset = {
  fontFamily: 'monospace, monospace',
  fontSize: '1em'
};
var smallReset = {
  fontSize: '80%'
};
var subSupReset = {
  fontSize: '75%',
  lineHeight: 0,
  position: 'relative',
  verticalAlign: 'baseline'
};

var subReset = _assign(_assign({}, subSupReset), {
  bottom: '-0.25em'
});

var supReset = _assign(_assign({}, subSupReset), {
  top: '-0.5em'
});

var imgReset = {
  borderStyle: 'none'
};
var formsReset = {
  fontFamily: 'inherit',
  fontSize: '100%',
  lineHeight: 1.15,
  margin: 0
};

var buttonReset = _assign(_assign({}, formsReset), {
  overflow: 'visible',
  textTransform: 'none',
  WebkitAppearance: 'button',
  '&[type="button"], &[type="reset"], &[type="submit"]': {
    WebkitAppearance: 'button'
  },
  '&::-moz-focus-inner, &[type="button"]::-moz-focus-inner, &[type="reset"]::-moz-focus-inner, &[type="submit"]::-moz-focus-inner': {
    borderStyle: 'none',
    padding: 0
  },
  '&:-moz-focusring, &[type="button"]:-moz-focusring, &[type="reset"]:-moz-focusring, &[type="submit"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  }
});

var inputReset = _assign(_assign({}, formsReset), {
  overflow: 'visible',
  '&[type="checkbox"], &[type="radio"]': {
    boxSizing: 'border-box',
    padding: 0
  },
  '&[type="number"]::-webkit-inner-spin-button, &[type="number"]::-webkit-outer-spin-button': {
    height: 'auto'
  },
  '&[type="search"]': {
    WebkitAppearance: 'textfield',
    outlineOffset: -2
  },
  '&[type="search"]::-webkit-search-decoration': {
    WebkitAppearance: 'none'
  },
  '&::-webkit-file-upload-button': {
    WebkitAppearance: 'button',
    font: 'inherit'
  }
});

var selectReset = _assign(_assign({}, formsReset), {
  textTransform: 'none'
});

var fieldsetReset = {
  padding: '0.35em 0.75em 0.625em'
};
var legendReset = {
  boxSizing: 'border-box',
  color: 'inherit',
  display: 'table',
  maxWidth: '100%',
  padding: 0,
  whiteSpace: 'normal'
};
var progressReset = {
  verticalAlign: 'baseline'
};

var textareaReset = _assign(_assign({}, formsReset), {
  overflow: 'auto'
});

var detailsReset = {
  display: 'block'
};
var summaryReset = {
  display: 'list-item'
};
var templateReset = {
  display: 'none'
};
var hiddenReset = {
  display: 'none'
};
var elementResetMap = {
  a: aReset,
  abbr: abbrReset,
  b: strongReset,
  body: bodyReset,
  button: buttonReset,
  code: codeReset,
  details: detailsReset,
  fieldset: fieldsetReset,
  h1: h1Reset,
  hr: hrReset,
  html: htmlReset,
  img: imgReset,
  input: inputReset,
  kbd: codeReset,
  legend: legendReset,
  main: mainReset,
  optgroup: formsReset,
  pre: preReset,
  progress: progressReset,
  samp: codeReset,
  select: selectReset,
  small: smallReset,
  strong: strongReset,
  sub: subReset,
  summary: summaryReset,
  sup: supReset,
  template: templateReset,
  textarea: textareaReset
};

var Any = function Any(_a) {
  var anyCss = _a.anyCss,
      is = _a.is,
      props = __rest(_a, ["anyCss", "is"]);

  var elementName = is || 'div';
  var elementReset = elementResetMap[elementName];
  return jsx(elementName, _assign({
    css: [anyReset, elementReset, anyCss]
  }, props), props.children);
};

var A = function A(props) {
  return jsx$1(Any, _assign({
    is: "a"
  }, props), void 0);
};

var Abbr = function Abbr(props) {
  return jsx$1(Any, _assign({
    is: "abbr"
  }, props), void 0);
};

var Address = function Address(props) {
  return jsx$1(Any, _assign({
    is: "address"
  }, props), void 0);
};

var Area = function Area(props) {
  return jsx$1(Any, _assign({
    is: "area"
  }, props), void 0);
};

var Article = function Article(props) {
  return jsx$1(Any, _assign({
    is: "article"
  }, props), void 0);
};

var Aside = function Aside(props) {
  return jsx$1(Any, _assign({
    is: "aside"
  }, props), void 0);
};

var Audio = function Audio(props) {
  return jsx$1(Any, _assign({
    is: "audio"
  }, props), void 0);
};

var B = function B(props) {
  return jsx$1(Any, _assign({
    is: "b"
  }, props), void 0);
};

var Base = function Base(props) {
  return jsx$1(Any, _assign({
    is: "base"
  }, props), void 0);
};

var Bdi = function Bdi(props) {
  return jsx$1(Any, _assign({
    is: "bdi"
  }, props), void 0);
};

var Bdo = function Bdo(props) {
  return jsx$1(Any, _assign({
    is: "bdo"
  }, props), void 0);
};

var Big = function Big(props) {
  return jsx$1(Any, _assign({
    is: "big"
  }, props), void 0);
};

var Blockquote = function Blockquote(props) {
  return jsx$1(Any, _assign({
    is: "blockquote"
  }, props), void 0);
};

var Body = function Body(props) {
  return jsx$1(Any, _assign({
    is: "body"
  }, props), void 0);
};

var Br = function Br(props) {
  return jsx$1(Any, _assign({
    is: "br"
  }, props), void 0);
};

var Button = function Button(props) {
  return jsx$1(Any, _assign({
    is: "button"
  }, props), void 0);
};

var Canvas = function Canvas(props) {
  return jsx$1(Any, _assign({
    is: "canvas"
  }, props), void 0);
};

var Caption = function Caption(props) {
  return jsx$1(Any, _assign({
    is: "caption"
  }, props), void 0);
};

var Cite = function Cite(props) {
  return jsx$1(Any, _assign({
    is: "cite"
  }, props), void 0);
};

var Code = function Code(props) {
  return jsx$1(Any, _assign({
    is: "code"
  }, props), void 0);
};

var Col = function Col(props) {
  return jsx$1(Any, _assign({
    is: "col"
  }, props), void 0);
};

var Colgroup = function Colgroup(props) {
  return jsx$1(Any, _assign({
    is: "colgroup"
  }, props), void 0);
};

var Data = function Data(props) {
  return jsx$1(Any, _assign({
    is: "data"
  }, props), void 0);
};

var Datalist = function Datalist(props) {
  return jsx$1(Any, _assign({
    is: "datalist"
  }, props), void 0);
};

var Dd = function Dd(props) {
  return jsx$1(Any, _assign({
    is: "dd"
  }, props), void 0);
};

var Del = function Del(props) {
  return jsx$1(Any, _assign({
    is: "del"
  }, props), void 0);
};

var Details = function Details(props) {
  return jsx$1(Any, _assign({
    is: "details"
  }, props), void 0);
};

var Dfn = function Dfn(props) {
  return jsx$1(Any, _assign({
    is: "dfn"
  }, props), void 0);
};

var Dialog = function Dialog(props) {
  return jsx$1(Any, _assign({
    is: "dialog"
  }, props), void 0);
};

var Div = function Div(props) {
  return jsx$1(Any, _assign({
    is: "div"
  }, props), void 0);
};

var Dl = function Dl(props) {
  return jsx$1(Any, _assign({
    is: "dl"
  }, props), void 0);
};

var Dt = function Dt(props) {
  return jsx$1(Any, _assign({
    is: "dt"
  }, props), void 0);
};

var Em = function Em(props) {
  return jsx$1(Any, _assign({
    is: "em"
  }, props), void 0);
};

var Embed = function Embed(props) {
  return jsx$1(Any, _assign({
    is: "embed"
  }, props), void 0);
};

var Fieldset = function Fieldset(props) {
  return jsx$1(Any, _assign({
    is: "fieldset"
  }, props), void 0);
};

var Figcaption = function Figcaption(props) {
  return jsx$1(Any, _assign({
    is: "figcaption"
  }, props), void 0);
};

var Figure = function Figure(props) {
  return jsx$1(Any, _assign({
    is: "figure"
  }, props), void 0);
};

var Footer = function Footer(props) {
  return jsx$1(Any, _assign({
    is: "footer"
  }, props), void 0);
};

var Form = function Form(props) {
  return jsx$1(Any, _assign({
    is: "form"
  }, props), void 0);
};

var H1 = function H1(props) {
  return jsx$1(Any, _assign({
    is: "h1"
  }, props), void 0);
};

var H2 = function H2(props) {
  return jsx$1(Any, _assign({
    is: "h2"
  }, props), void 0);
};

var H3 = function H3(props) {
  return jsx$1(Any, _assign({
    is: "h3"
  }, props), void 0);
};

var H4 = function H4(props) {
  return jsx$1(Any, _assign({
    is: "h4"
  }, props), void 0);
};

var H5 = function H5(props) {
  return jsx$1(Any, _assign({
    is: "h5"
  }, props), void 0);
};

var H6 = function H6(props) {
  return jsx$1(Any, _assign({
    is: "h6"
  }, props), void 0);
};

var Head = function Head(props) {
  return jsx$1(Any, _assign({
    is: "head"
  }, props), void 0);
};

var Header = function Header(props) {
  return jsx$1(Any, _assign({
    is: "header"
  }, props), void 0);
};

var Hgroup = function Hgroup(props) {
  return jsx$1(Any, _assign({
    is: "hgroup"
  }, props), void 0);
};

var Hr = function Hr(props) {
  return jsx$1(Any, _assign({
    is: "hr"
  }, props), void 0);
};

var Html = function Html(props) {
  return jsx$1(Any, _assign({
    is: "html"
  }, props), void 0);
};

var I = function I(props) {
  return jsx$1(Any, _assign({
    is: "i"
  }, props), void 0);
};

var Iframe = function Iframe(props) {
  return jsx$1(Any, _assign({
    is: "iframe"
  }, props), void 0);
};

var Img = function Img(props) {
  return jsx$1(Any, _assign({
    is: "img"
  }, props), void 0);
};

var Input = function Input(props) {
  return jsx$1(Any, _assign({
    is: "input"
  }, props), void 0);
};

var Ins = function Ins(props) {
  return jsx$1(Any, _assign({
    is: "ins"
  }, props), void 0);
};

var Kbd = function Kbd(props) {
  return jsx$1(Any, _assign({
    is: "kbd"
  }, props), void 0);
};

var Keygen = function Keygen(props) {
  return jsx$1(Any, _assign({
    is: "keygen"
  }, props), void 0);
};

var Label = function Label(props) {
  return jsx$1(Any, _assign({
    is: "label"
  }, props), void 0);
};

var Legend = function Legend(props) {
  return jsx$1(Any, _assign({
    is: "legend"
  }, props), void 0);
};

var Li = function Li(props) {
  return jsx$1(Any, _assign({
    is: "li"
  }, props), void 0);
};

var Link = function Link(props) {
  return jsx$1(Any, _assign({
    is: "link"
  }, props), void 0);
};

var Main = function Main(props) {
  return jsx$1(Any, _assign({
    is: "main"
  }, props), void 0);
};

var Map = function Map(props) {
  return jsx$1(Any, _assign({
    is: "map"
  }, props), void 0);
};

var Mark = function Mark(props) {
  return jsx$1(Any, _assign({
    is: "mark"
  }, props), void 0);
};

var Menu = function Menu(props) {
  return jsx$1(Any, _assign({
    is: "menu"
  }, props), void 0);
};

var Menuitem = function Menuitem(props) {
  return jsx$1(Any, _assign({
    is: "menuitem"
  }, props), void 0);
};

var Meta = function Meta(props) {
  return jsx$1(Any, _assign({
    is: "meta"
  }, props), void 0);
};

var Meter = function Meter(props) {
  return jsx$1(Any, _assign({
    is: "meter"
  }, props), void 0);
};

var Nav = function Nav(props) {
  return jsx$1(Any, _assign({
    is: "nav"
  }, props), void 0);
};

var Noindex = function Noindex(props) {
  return jsx$1(Any, _assign({
    is: "noindex"
  }, props), void 0);
};

var Noscript = function Noscript(props) {
  return jsx$1(Any, _assign({
    is: "noscript"
  }, props), void 0);
};

var Object$1 = function Object$1(props) {
  return jsx$1(Any, _assign({
    is: "object"
  }, props), void 0);
};

var Ol = function Ol(props) {
  return jsx$1(Any, _assign({
    is: "ol"
  }, props), void 0);
};

var Optgroup = function Optgroup(props) {
  return jsx$1(Any, _assign({
    is: "optgroup"
  }, props), void 0);
};

var Option = function Option(props) {
  return jsx$1(Any, _assign({
    is: "option"
  }, props), void 0);
};

var Output = function Output(props) {
  return jsx$1(Any, _assign({
    is: "output"
  }, props), void 0);
};

var P = function P(props) {
  return jsx$1(Any, _assign({
    is: "p"
  }, props), void 0);
};

var Param = function Param(props) {
  return jsx$1(Any, _assign({
    is: "param"
  }, props), void 0);
};

var Picture = function Picture(props) {
  return jsx$1(Any, _assign({
    is: "picture"
  }, props), void 0);
};

var Pre = function Pre(props) {
  return jsx$1(Any, _assign({
    is: "pre"
  }, props), void 0);
};

var Progress = function Progress(props) {
  return jsx$1(Any, _assign({
    is: "progress"
  }, props), void 0);
};

var Q = function Q(props) {
  return jsx$1(Any, _assign({
    is: "q"
  }, props), void 0);
};

var Rp = function Rp(props) {
  return jsx$1(Any, _assign({
    is: "rp"
  }, props), void 0);
};

var Rt = function Rt(props) {
  return jsx$1(Any, _assign({
    is: "rt"
  }, props), void 0);
};

var Ruby = function Ruby(props) {
  return jsx$1(Any, _assign({
    is: "ruby"
  }, props), void 0);
};

var S = function S(props) {
  return jsx$1(Any, _assign({
    is: "s"
  }, props), void 0);
};

var Samp = function Samp(props) {
  return jsx$1(Any, _assign({
    is: "samp"
  }, props), void 0);
};

var Slot = function Slot(props) {
  return jsx$1(Any, _assign({
    is: "slot"
  }, props), void 0);
};

var Script = function Script(props) {
  return jsx$1(Any, _assign({
    is: "script"
  }, props), void 0);
};

var Section = function Section(props) {
  return jsx$1(Any, _assign({
    is: "section"
  }, props), void 0);
};

var Select = function Select(props) {
  return jsx$1(Any, _assign({
    is: "select"
  }, props), void 0);
};

var Small = function Small(props) {
  return jsx$1(Any, _assign({
    is: "small"
  }, props), void 0);
};

var Source = function Source(props) {
  return jsx$1(Any, _assign({
    is: "source"
  }, props), void 0);
};

var Span = function Span(props) {
  return jsx$1(Any, _assign({
    is: "span"
  }, props), void 0);
};

var Strong = function Strong(props) {
  return jsx$1(Any, _assign({
    is: "strong"
  }, props), void 0);
};

var Style = function Style(props) {
  return jsx$1(Any, _assign({
    is: "style"
  }, props), void 0);
};

var Sub = function Sub(props) {
  return jsx$1(Any, _assign({
    is: "sub"
  }, props), void 0);
};

var Summary = function Summary(props) {
  return jsx$1(Any, _assign({
    is: "summary"
  }, props), void 0);
};

var Sup = function Sup(props) {
  return jsx$1(Any, _assign({
    is: "sup"
  }, props), void 0);
};

var Table = function Table(props) {
  return jsx$1(Any, _assign({
    is: "table"
  }, props), void 0);
};

var Template = function Template(props) {
  return jsx$1(Any, _assign({
    is: "template"
  }, props), void 0);
};

var Tbody = function Tbody(props) {
  return jsx$1(Any, _assign({
    is: "tbody"
  }, props), void 0);
};

var Td = function Td(props) {
  return jsx$1(Any, _assign({
    is: "td"
  }, props), void 0);
};

var Textarea = function Textarea(props) {
  return jsx$1(Any, _assign({
    is: "textarea"
  }, props), void 0);
};

var Tfoot = function Tfoot(props) {
  return jsx$1(Any, _assign({
    is: "tfoot"
  }, props), void 0);
};

var Th = function Th(props) {
  return jsx$1(Any, _assign({
    is: "th"
  }, props), void 0);
};

var Thead = function Thead(props) {
  return jsx$1(Any, _assign({
    is: "thead"
  }, props), void 0);
};

var Time = function Time(props) {
  return jsx$1(Any, _assign({
    is: "time"
  }, props), void 0);
};

var Title = function Title(props) {
  return jsx$1(Any, _assign({
    is: "title"
  }, props), void 0);
};

var Tr = function Tr(props) {
  return jsx$1(Any, _assign({
    is: "tr"
  }, props), void 0);
};

var Track = function Track(props) {
  return jsx$1(Any, _assign({
    is: "track"
  }, props), void 0);
};

var U = function U(props) {
  return jsx$1(Any, _assign({
    is: "u"
  }, props), void 0);
};

var Ul = function Ul(props) {
  return jsx$1(Any, _assign({
    is: "ul"
  }, props), void 0);
};

var Var = function Var(props) {
  return jsx$1(Any, _assign({
    is: "var"
  }, props), void 0);
};

var Video = function Video(props) {
  return jsx$1(Any, _assign({
    is: "video"
  }, props), void 0);
};

var Wbr = function Wbr(props) {
  return jsx$1(Any, _assign({
    is: "wbr"
  }, props), void 0);
};

var Webview = function Webview(props) {
  return jsx$1(Any, _assign({
    is: "webview"
  }, props), void 0);
};

var Svg = function Svg(props) {
  return jsx$1(Any, _assign({
    is: "svg"
  }, props), void 0);
};

var Animate = function Animate(props) {
  return jsx$1(Any, _assign({
    is: "animate"
  }, props), void 0);
};

var AnimateMotion = function AnimateMotion(props) {
  return jsx$1(Any, _assign({
    is: "animateMotion"
  }, props), void 0);
};

var AnimateTransform = function AnimateTransform(props) {
  return jsx$1(Any, _assign({
    is: "animateTransform"
  }, props), void 0);
};

var Circle = function Circle(props) {
  return jsx$1(Any, _assign({
    is: "circle"
  }, props), void 0);
};

var ClipPath = function ClipPath(props) {
  return jsx$1(Any, _assign({
    is: "clipPath"
  }, props), void 0);
};

var Defs = function Defs(props) {
  return jsx$1(Any, _assign({
    is: "defs"
  }, props), void 0);
};

var Desc = function Desc(props) {
  return jsx$1(Any, _assign({
    is: "desc"
  }, props), void 0);
};

var Ellipse = function Ellipse(props) {
  return jsx$1(Any, _assign({
    is: "ellipse"
  }, props), void 0);
};

var FeBlend = function FeBlend(props) {
  return jsx$1(Any, _assign({
    is: "feBlend"
  }, props), void 0);
};

var FeColorMatrix = function FeColorMatrix(props) {
  return jsx$1(Any, _assign({
    is: "feColorMatrix"
  }, props), void 0);
};

var FeComponentTransfer = function FeComponentTransfer(props) {
  return jsx$1(Any, _assign({
    is: "feComponentTransfer"
  }, props), void 0);
};

var FeComposite = function FeComposite(props) {
  return jsx$1(Any, _assign({
    is: "feComposite"
  }, props), void 0);
};

var FeConvolveMatrix = function FeConvolveMatrix(props) {
  return jsx$1(Any, _assign({
    is: "feConvolveMatrix"
  }, props), void 0);
};

var FeDiffuseLighting = function FeDiffuseLighting(props) {
  return jsx$1(Any, _assign({
    is: "feDiffuseLighting"
  }, props), void 0);
};

var FeDisplacementMap = function FeDisplacementMap(props) {
  return jsx$1(Any, _assign({
    is: "feDisplacementMap"
  }, props), void 0);
};

var FeDistantLight = function FeDistantLight(props) {
  return jsx$1(Any, _assign({
    is: "feDistantLight"
  }, props), void 0);
};

var FeDropShadow = function FeDropShadow(props) {
  return jsx$1(Any, _assign({
    is: "feDropShadow"
  }, props), void 0);
};

var FeFlood = function FeFlood(props) {
  return jsx$1(Any, _assign({
    is: "feFlood"
  }, props), void 0);
};

var FeFuncA = function FeFuncA(props) {
  return jsx$1(Any, _assign({
    is: "feFuncA"
  }, props), void 0);
};

var FeFuncB = function FeFuncB(props) {
  return jsx$1(Any, _assign({
    is: "feFuncB"
  }, props), void 0);
};

var FeFuncG = function FeFuncG(props) {
  return jsx$1(Any, _assign({
    is: "feFuncG"
  }, props), void 0);
};

var FeFuncR = function FeFuncR(props) {
  return jsx$1(Any, _assign({
    is: "feFuncR"
  }, props), void 0);
};

var FeGaussianBlur = function FeGaussianBlur(props) {
  return jsx$1(Any, _assign({
    is: "feGaussianBlur"
  }, props), void 0);
};

var FeImage = function FeImage(props) {
  return jsx$1(Any, _assign({
    is: "feImage"
  }, props), void 0);
};

var FeMerge = function FeMerge(props) {
  return jsx$1(Any, _assign({
    is: "feMerge"
  }, props), void 0);
};

var FeMergeNode = function FeMergeNode(props) {
  return jsx$1(Any, _assign({
    is: "feMergeNode"
  }, props), void 0);
};

var FeMorphology = function FeMorphology(props) {
  return jsx$1(Any, _assign({
    is: "feMorphology"
  }, props), void 0);
};

var FeOffset = function FeOffset(props) {
  return jsx$1(Any, _assign({
    is: "feOffset"
  }, props), void 0);
};

var FePointLight = function FePointLight(props) {
  return jsx$1(Any, _assign({
    is: "fePointLight"
  }, props), void 0);
};

var FeSpecularLighting = function FeSpecularLighting(props) {
  return jsx$1(Any, _assign({
    is: "feSpecularLighting"
  }, props), void 0);
};

var FeSpotLight = function FeSpotLight(props) {
  return jsx$1(Any, _assign({
    is: "feSpotLight"
  }, props), void 0);
};

var FeTile = function FeTile(props) {
  return jsx$1(Any, _assign({
    is: "feTile"
  }, props), void 0);
};

var FeTurbulence = function FeTurbulence(props) {
  return jsx$1(Any, _assign({
    is: "feTurbulence"
  }, props), void 0);
};

var Filter = function Filter(props) {
  return jsx$1(Any, _assign({
    is: "filter"
  }, props), void 0);
};

var ForeignObject = function ForeignObject(props) {
  return jsx$1(Any, _assign({
    is: "foreignObject"
  }, props), void 0);
};

var G = function G(props) {
  return jsx$1(Any, _assign({
    is: "g"
  }, props), void 0);
};

var Image = function Image(props) {
  return jsx$1(Any, _assign({
    is: "image"
  }, props), void 0);
};

var Line = function Line(props) {
  return jsx$1(Any, _assign({
    is: "line"
  }, props), void 0);
};

var LinearGradient = function LinearGradient(props) {
  return jsx$1(Any, _assign({
    is: "linearGradient"
  }, props), void 0);
};

var Marker = function Marker(props) {
  return jsx$1(Any, _assign({
    is: "marker"
  }, props), void 0);
};

var Mask = function Mask(props) {
  return jsx$1(Any, _assign({
    is: "mask"
  }, props), void 0);
};

var Metadata = function Metadata(props) {
  return jsx$1(Any, _assign({
    is: "metadata"
  }, props), void 0);
};

var Mpath = function Mpath(props) {
  return jsx$1(Any, _assign({
    is: "mpath"
  }, props), void 0);
};

var Path = function Path(props) {
  return jsx$1(Any, _assign({
    is: "path"
  }, props), void 0);
};

var Pattern = function Pattern(props) {
  return jsx$1(Any, _assign({
    is: "pattern"
  }, props), void 0);
};

var Polygon = function Polygon(props) {
  return jsx$1(Any, _assign({
    is: "polygon"
  }, props), void 0);
};

var Polyline = function Polyline(props) {
  return jsx$1(Any, _assign({
    is: "polyline"
  }, props), void 0);
};

var RadialGradient = function RadialGradient(props) {
  return jsx$1(Any, _assign({
    is: "radialGradient"
  }, props), void 0);
};

var Rect = function Rect(props) {
  return jsx$1(Any, _assign({
    is: "rect"
  }, props), void 0);
};

var Stop = function Stop(props) {
  return jsx$1(Any, _assign({
    is: "stop"
  }, props), void 0);
};

var Switch = function Switch(props) {
  return jsx$1(Any, _assign({
    is: "switch"
  }, props), void 0);
};

var Symbol = function Symbol(props) {
  return jsx$1(Any, _assign({
    is: "symbol"
  }, props), void 0);
};

var Text = function Text(props) {
  return jsx$1(Any, _assign({
    is: "text"
  }, props), void 0);
};

var TextPath = function TextPath(props) {
  return jsx$1(Any, _assign({
    is: "textPath"
  }, props), void 0);
};

var Tspan = function Tspan(props) {
  return jsx$1(Any, _assign({
    is: "tspan"
  }, props), void 0);
};

var Use = function Use(props) {
  return jsx$1(Any, _assign({
    is: "use"
  }, props), void 0);
};

var View = function View(props) {
  return jsx$1(Any, _assign({
    is: "view"
  }, props), void 0);
};

var globalReset = {
  html: htmlReset,
  body: bodyReset,
  '[hidden]': hiddenReset
};
export { A, Abbr, Address, Animate, AnimateMotion, AnimateTransform, Any, Area, Article, Aside, Audio, B, Base, Bdi, Bdo, Big, Blockquote, Body, Br, Button, Canvas, Caption, Circle, Cite, ClipPath, Code, Col, Colgroup, Data, Datalist, Dd, Defs, Del, Desc, Details, Dfn, Dialog, Div, Dl, Dt, Ellipse, Em, Embed, FeBlend, FeColorMatrix, FeComponentTransfer, FeComposite, FeConvolveMatrix, FeDiffuseLighting, FeDisplacementMap, FeDistantLight, FeDropShadow, FeFlood, FeFuncA, FeFuncB, FeFuncG, FeFuncR, FeGaussianBlur, FeImage, FeMerge, FeMergeNode, FeMorphology, FeOffset, FePointLight, FeSpecularLighting, FeSpotLight, FeTile, FeTurbulence, Fieldset, Figcaption, Figure, Filter, Footer, ForeignObject, Form, G, H1, H2, H3, H4, H5, H6, Head, Header, Hgroup, Hr, Html, I, Iframe, Image, Img, Input, Ins, Kbd, Keygen, Label, Legend, Li, Line, LinearGradient, Link, Main, Map, Mark, Marker, Mask, Menu, Menuitem, Meta, Metadata, Meter, Mpath, Nav, Noindex, Noscript, Object$1 as Object, Ol, Optgroup, Option, Output, P, Param, Path, Pattern, Picture, Polygon, Polyline, Pre, Progress, Q, RadialGradient, Rect, Rp, Rt, Ruby, S, Samp, Script, Section, Select, Slot, Small, Source, Span, Stop, Strong, Style, Sub, Summary, Sup, Svg, Switch, Symbol, Table, Tbody, Td, Template, Text, TextPath, Textarea, Tfoot, Th, Thead, Time, Title, Tr, Track, Tspan, U, Ul, Use, Var, Video, View, Wbr, Webview, anyReset, bodyReset, elementResetMap, globalReset, hiddenReset, htmlReset };